/**
 * Seller Watch — pure list/activity helpers. No network access.
 *
 * Activity = new Walmart item IDs detected per day from real Storefront Hunter
 * catalog checks. Days before monitoring started are "not monitored", never 0.
 */

export const ACTIVITY_DAYS = 14;

export const FILTER_OPTIONS = [
  ["all", "All Sellers"],
  ["active", "Active"],
  ["paused", "Paused"],
  ["saved", "Saved"],
  ["tagged", "Tagged"],
  ["activity", "New Activity"],
  ["100", "100+ Products"],
  ["500", "500+ Products"],
  ["1000", "1,000+ Products"],
  ["today", "Visited Today"],
  ["week", "Last 7 Days"],
  ["month", "Last 30 Days"],
];

export const SORT_OPTIONS = [
  ["recent", "Recently Visited"],
  ["new", "Most New Products"],
  ["products", "Most Products"],
  ["reviews", "Most Reviews"],
  ["rating", "Highest Rating"],
  ["rating_low", "Lowest Rating"],
  ["az", "Seller Name A–Z"],
  ["za", "Seller Name Z–A"],
];

const DAY_MS = 24 * 60 * 60 * 1000;

function toDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function localDayKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function formatDayLabel(date) {
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** 14 local-calendar days ending today, each with the count of new products detected. */
export function buildDailyActivity(seller, now = new Date(), days = ACTIVITY_DAYS) {
  const counts = new Map();
  for (const ev of seller?.activity_events || []) {
    const at = toDate(ev?.detected_at);
    if (!at) continue;
    const key = localDayKey(at);
    counts.set(key, (counts.get(key) || 0) + (Number(ev.count) || 0));
  }
  const since = toDate(seller?.monitoring_since);
  const sinceDay = since ? startOfLocalDay(since).getTime() : null;
  const today = startOfLocalDay(now);
  const out = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const day = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    const key = localDayKey(day);
    out.push({
      key,
      label: formatDayLabel(day),
      count: counts.get(key) || 0,
      monitored: sinceDay != null && day.getTime() >= sinceDay,
    });
  }
  return out;
}

export function productsPostedLabel(count) {
  return `${count} ${count === 1 ? "product" : "products"} posted`;
}

export function formatBadgeCount(n) {
  const v = Number(n) || 0;
  if (v <= 0) return null;
  return v > 99 ? "99+" : String(v);
}

export function daysSince(value, now = new Date()) {
  const d = toDate(value);
  if (!d) return null;
  return Math.floor((startOfLocalDay(now) - startOfLocalDay(d)) / DAY_MS);
}

export function relativeTime(value, now = new Date()) {
  const d = toDate(value);
  if (!d) return null;
  const mins = Math.max(0, Math.round((now - d) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const daysAgo = Math.round(hours / 24);
  return `${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`;
}

function matchesFilter(s, filter, now) {
  const products = Number(s.product_count) || 0;
  const visited = daysSince(s.last_visited_at, now);
  switch (filter) {
    case "active":
      return (s.status || "active") === "active";
    case "paused":
      return s.status === "paused";
    case "saved":
      return !!s.saved;
    case "tagged":
      return (s.tags || []).length > 0;
    case "activity":
      return (Number(s.new_count) || 0) > 0;
    case "100":
      return products >= 100;
    case "500":
      return products >= 500;
    case "1000":
      return products >= 1000;
    case "today":
      return visited === 0;
    case "week":
      return visited != null && visited <= 7;
    case "month":
      return visited != null && visited <= 30;
    default:
      return true;
  }
}

const num = (v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v));
const nameOf = (s) => String(s.seller_name || s.seller_id || "");
const byNullsLast = (a, b, dir) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return dir * (a - b);
};

const SORTERS = {
  recent: (a, b) => String(b.last_visited_at || "").localeCompare(String(a.last_visited_at || "")),
  new: (a, b) => (Number(b.new_count) || 0) - (Number(a.new_count) || 0),
  products: (a, b) => byNullsLast(num(a.product_count), num(b.product_count), -1),
  reviews: (a, b) => byNullsLast(num(a.review_count), num(b.review_count), -1),
  rating: (a, b) => byNullsLast(num(a.rating), num(b.rating), -1),
  rating_low: (a, b) => byNullsLast(num(a.rating), num(b.rating), 1),
  az: (a, b) => nameOf(a).localeCompare(nameOf(b)),
  za: (a, b) => nameOf(b).localeCompare(nameOf(a)),
};

export function visibleSellers(sellers, { query = "", tab = "all", filter = "all", sort = "recent" } = {}, now = new Date()) {
  const q = query.trim().toLowerCase();
  const list = (sellers || []).filter((s) => {
    if (q) {
      const text = [s.seller_name, s.seller_id, ...(s.tags || [])].join(" ").toLowerCase();
      if (!text.includes(q)) return false;
    }
    if (tab === "activity" && !((Number(s.new_count) || 0) > 0)) return false;
    return matchesFilter(s, filter, now);
  });
  return list.sort(SORTERS[sort] || SORTERS.recent);
}

export function unreadTotal(sellers) {
  return (sellers || []).reduce(
    (sum, s) => sum + ((s.status || "active") === "active" ? Number(s.new_count) || 0 : 0),
    0
  );
}

export function addTagUnique(tags, tag) {
  const clean = String(tag || "").replace(/\s+/g, " ").trim().slice(0, 32).trim();
  if (!clean) return tags || [];
  const list = tags || [];
  if (list.some((t) => t.toLowerCase() === clean.toLowerCase())) return list;
  return [...list, clean];
}

export function allKnownTags(sellers) {
  const seen = new Map();
  for (const s of sellers || []) {
    for (const t of s.tags || []) {
      if (!seen.has(t.toLowerCase())) seen.set(t.toLowerCase(), t);
    }
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}
